import { useEffect, useState } from 'react';
import { api } from './api';
import type { ApiEnvelope } from './types';

type AccountSession =
  | { state: 'unknown' }
  | { state: 'anonymous' }
  | { state: 'signed_in'; email: string };

const SESSION_CHANGED = 'askrs:account-session-changed';

/**
 * Tells the shell (and anything else that cares) to re-check the account session.
 *
 * Sign-in and sign-out happen inside a route, not on a page load, so without
 * this the header would keep offering "로그인" to someone who just signed in.
 */
export function notifyAccountSessionChanged(): void {
  window.dispatchEvent(new Event(SESSION_CHANGED));
}

/** Probes the optional account session. A 401 is the normal anonymous case. */
export function useAccountSession(): AccountSession {
  const [session, setSession] = useState<AccountSession>({ state: 'unknown' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const bump = () => setNonce((value) => value + 1);
    window.addEventListener(SESSION_CHANGED, bump);

    return () => window.removeEventListener(SESSION_CHANGED, bump);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    api.account('/me', { signal: controller.signal })
      .then((envelope) => {
        const data = (envelope as ApiEnvelope<{ account: { email: string } }>).data;
        setSession({ state: 'signed_in', email: data.account.email });
      })
      .catch(() => {
        if (!controller.signal.aborted) setSession({ state: 'anonymous' });
      });

    return () => controller.abort();
  }, [nonce]);

  return session;
}
